// Geselecteerde video's in een verzameling zetten: kies een bestaande of maak
// er ter plekke een. Een verzameling is van het team — iedereen mag toevoegen.
import { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItemButton, ListItemText, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const API = `${AI_ENGINE_URL}/api/v1/inspiration`;

export default function VerzamelingDialog({ videoIds, verzamelingen, onKlaar, onClose }) {
  const [nieuw, setNieuw] = useState('');
  const [bezig, setBezig] = useState(false);
  const [lijst, setLijst] = useState(verzamelingen || null);

  useEffect(() => {
    if (lijst) return;
    (async () => {
      try {
        const r = await authFetch(`${API}/collections`);
        const d = await r.json();
        setLijst(d.collections || []);
      } catch { setLijst([]); }
    })();
  }, [lijst]);

  const voegToe = async (verzameling) => {
    setBezig(true);
    try {
      const r = await authFetch(`${API}/collections/${verzameling.id}/videos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoIds }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(d.toegevoegd ? `${d.toegevoegd} toegevoegd aan "${verzameling.naam}"` : `Stond al in "${verzameling.naam}"`);
      onKlaar?.(d.collection);
      onClose();
    } catch (e) { toast.error(`Toevoegen mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };

  const maak = async () => {
    if (!nieuw.trim()) return;
    setBezig(true);
    try {
      const r = await authFetch(`${API}/collections`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naam: nieuw.trim(), videoIds }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Verzameling "${d.collection.naam}" gemaakt`);
      onKlaar?.(d.collection);
      onClose();
    } catch (e) { toast.error(`Maken mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {videoIds.length} video{videoIds.length === 1 ? '' : "'s"} in een verzameling
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField size="small" fullWidth autoFocus placeholder="Nieuwe verzameling, bijv. 'Referenties Enexis'"
            value={nieuw} onChange={(e) => setNieuw(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') maak(); }} />
          <Button variant="contained" onClick={maak} disabled={bezig || !nieuw.trim()} startIcon={<AddIcon />}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
            Maak
          </Button>
        </Box>
        {lijst === null && <CircularProgress size={20} />}
        {lijst?.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary">Of voeg toe aan een bestaande</Typography>
            <List dense disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {lijst.map((c) => (
                <ListItemButton key={c.id} disabled={bezig} onClick={() => voegToe(c)} sx={{ borderRadius: 1 }}>
                  <ListItemText primary={c.naam}
                    secondary={`${c.aantal} video${c.aantal === 1 ? '' : "'s"}${c.door ? ` · ${c.door.split('@')[0]}` : ''}`} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
      </DialogActions>
    </Dialog>
  );
}
